import { TaxonomyCategory, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { ClientTaxonomyEntity, TaxonomyTermService } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import {
  ITaxonomyRouteState,
  parseTaxonomyManagementRoute,
  taxonomyFacetValue,
  taxonomyIdentity,
  taxonomyPath,
} from './GovernanceTaxonomyManagement.utils';

interface ITaxonomyManagementContextValue {
  loadingTaxonomies: boolean;
  taxonomies: ClientTaxonomyEntity[];
  groupedTaxonomies: Map<TaxonomyCategory, ClientTaxonomyEntity[]>;
  selectedTaxonomy?: ClientTaxonomyEntity;
  selectedTaxonomyIdentity?: string;
  setSelectedTaxonomyIdentity: (identity: string | undefined) => void;
  unapprovedCountsByTaxonomy: Record<string, number>;
  routeState: ITaxonomyRouteState;
  selectTaxonomy: (taxonomy: ClientTaxonomyEntity) => Promise<void>;
  reloadTaxonomies: () => Promise<void>;
}

const GovernanceTaxonomyManagementContext = React.createContext<ITaxonomyManagementContextValue | undefined>(undefined);

export function useGovernanceTaxonomyManagementContext() {
  const value = React.useContext(GovernanceTaxonomyManagementContext);
  if (!value) {
    throw new Error('useGovernanceTaxonomyManagementContext must be used within GovernanceTaxonomyManagementProvider.');
  }

  return value;
}

interface IProps {
  children: React.ReactNode;
}

export function GovernanceTaxonomyManagementProvider(props: IProps) {
  const apiContext = React.useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  const location = useLocation();
  const navigate = useNavigate();

  const routeState = React.useMemo(() => parseTaxonomyManagementRoute(location.pathname), [location.pathname]);

  const [loadingTaxonomies, setLoadingTaxonomies] = React.useState(true);
  const [taxonomies, setTaxonomies] = React.useState<ClientTaxonomyEntity[]>([]);
  const [selectedTaxonomyIdentity, setSelectedTaxonomyIdentity] = React.useState<string | undefined>(undefined);
  const [unapprovedCountsByTaxonomy, setUnapprovedCountsByTaxonomy] = React.useState<Record<string, number>>({});

  const requestIdRef = React.useRef(0);

  const taxonomyTermService = React.useMemo(() => {
    if (!api) {
      return undefined;
    }

    return api.service.taxonomyTerms as TaxonomyTermService;
  }, [api]);

  const groupedTaxonomies = React.useMemo(() => {
    const grouped = new Map<TaxonomyCategory, ClientTaxonomyEntity[]>();
    for (const taxonomy of taxonomies) {
      const category = taxonomy.category as TaxonomyCategory;
      const existing = grouped.get(category) || [];
      existing.push(taxonomy);
      grouped.set(category, existing);
    }

    return grouped;
  }, [taxonomies]);

  const selectedTaxonomy = React.useMemo(
    () => taxonomies.find((entry) => taxonomyIdentity(entry) === selectedTaxonomyIdentity) || taxonomies[0],
    [taxonomies, selectedTaxonomyIdentity]
  );

  const reloadTaxonomies = React.useCallback(async () => {
    if (!api || !taxonomyTermService) {
      setLoadingTaxonomies(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoadingTaxonomies(true);

    const taxonomyResult = await api.service.taxonomies.list({ page: 0, itemsPerPage: 500 });
    const loadedTaxonomies = taxonomyResult.items || [];

    if (requestId !== requestIdRef.current) {
      return;
    }

    // Surface the taxonomy list (e.g. a freshly added one in the sidebar) before the
    // per-taxonomy unapproved-count requests below resolve, so it appears immediately.
    setTaxonomies(loadedTaxonomies);
    setLoadingTaxonomies(false);

    const unapprovedCounts = await Promise.all(
      loadedTaxonomies.map(async (taxonomy) => {
        const identity = taxonomyIdentity(taxonomy);
        const facet = taxonomyFacetValue(taxonomy);

        if (!identity || !facet) {
          return undefined;
        }

        const result = await taxonomyTermService.list(
          { page: 0, itemsPerPage: 0 },
          {
            taxonomyFacet: facet,
            statuses: [TaxonomyTermStatus.Unapproved],
          }
        );

        return [identity, result.totalNumberOfItems || 0] as const;
      })
    );

    const nextUnapprovedCounts = Object.fromEntries(
      unapprovedCounts.filter((entry): entry is readonly [string, number] => Boolean(entry && entry[1] > 0))
    );

    if (requestId !== requestIdRef.current) {
      return;
    }

    setUnapprovedCountsByTaxonomy(nextUnapprovedCounts);
  }, [api, taxonomyTermService]);

  React.useEffect(() => {
    void reloadTaxonomies();

    return () => {
      // Invalidate in-flight loads on unmount or dependency change.
      requestIdRef.current += 1;
    };
  }, [reloadTaxonomies]);

  React.useEffect(() => {
    if (!taxonomies.length) {
      return;
    }

    const routeFacet = routeState.facet;
    const taxonomyFromRoute = routeFacet ? taxonomies.find((taxonomy) => taxonomyFacetValue(taxonomy) === routeFacet) : undefined;
    const nextSelected = taxonomyFromRoute || taxonomies[0];
    const nextSelectedIdentity = taxonomyIdentity(nextSelected);

    if (nextSelectedIdentity !== selectedTaxonomyIdentity) {
      setSelectedTaxonomyIdentity(nextSelectedIdentity);
    }
  }, [routeState.facet, selectedTaxonomyIdentity, taxonomies]);

  const selectTaxonomy = React.useCallback(
    async (taxonomy: ClientTaxonomyEntity) => {
      const nextIdentity = taxonomyIdentity(taxonomy);
      setSelectedTaxonomyIdentity(nextIdentity);

      const nextPath = taxonomyPath(taxonomyFacetValue(taxonomy));
      if (location.pathname !== nextPath) {
        navigate(nextPath);
      }
    },
    [location.pathname, navigate]
  );

  const value = React.useMemo<ITaxonomyManagementContextValue>(
    () => ({
      loadingTaxonomies,
      taxonomies,
      groupedTaxonomies,
      selectedTaxonomy,
      selectedTaxonomyIdentity,
      setSelectedTaxonomyIdentity,
      unapprovedCountsByTaxonomy,
      routeState,
      selectTaxonomy,
      reloadTaxonomies,
    }),
    [
      groupedTaxonomies,
      loadingTaxonomies,
      reloadTaxonomies,
      routeState,
      selectTaxonomy,
      selectedTaxonomy,
      selectedTaxonomyIdentity,
      taxonomies,
      unapprovedCountsByTaxonomy,
    ]
  );

  return <GovernanceTaxonomyManagementContext.Provider value={value}>{props.children}</GovernanceTaxonomyManagementContext.Provider>;
}
